'use client';

import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import parse from 'html-react-parser';

import { cn } from '@/lib/utils';
import { parseHtmlWithStyledLinks } from "@/utils/parseHtmlWithStyledLinks";

import H1 from '@/components/atoms/Typography/H1';
import H2 from '@/components/atoms/Typography/H2';
import H3 from '@/components/atoms/Typography/H3';
import H4 from '@/components/atoms/Typography/H4';
import H5 from '@/components/atoms/Typography/H5';
import H6 from '@/components/atoms/Typography/H6';
import { P } from '@/components/atoms/Typography/P';
import { Title } from '@/components/atoms/Typography/Title';

import type {
    HeadingConfigProps,
    MarketingTextProps,
    ParagraphConfigProps,
    TitleTextProps,
} from './ContentText.types';

export interface ContentTextProps extends HTMLAttributes<HTMLDivElement> {
    title?: string;
    subtitle?: string;
    textConfig?: (
        | TitleTextProps
        | HeadingConfigProps
        | ParagraphConfigProps
        | MarketingTextProps
    )[];
    'data-test-id'?: string;
}

const ContentText = forwardRef<HTMLDivElement, ContentTextProps>(
    ({ title, subtitle, textConfig, ...props }, ref) => (
        <div
            ref={ref}
            {...props}
        >
            {title ? <H2>{title}</H2> : null}

            {subtitle ? <H3>{subtitle}</H3> : null}

            {textConfig &&
                textConfig.map(({ text, type, ...elementProps }) => {
                    if (!text) {
                        return null;
                    }

                    switch (type) {
                        case 'heading1':
                            return (
                                <H1
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </H1>
                            );

                        case 'heading2':
                            return (
                                <H2
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </H2>
                            );

                        case 'heading3':
                            return (
                                <H3
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </H3>
                            );
                        case 'heading4':
                            return (
                                <H4
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </H4>
                            );

                        case 'heading5':
                            return (
                                <H5
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </H5>
                            );

                        case 'heading6':
                            return (
                                <H6
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </H6>
                            );

                        case 'marketingText':
                            const {
                                className: marketingTextClassName,
                                ...marketingTextProps
                            } = elementProps;

                            return (
                                <H4
                                    {...marketingTextProps}
                                    className={cn(
                                        'text-green-600 [&>span]:bg-green-600 [&>span]:text-white',
                                        marketingTextClassName,
                                    )}
                                >
                                    {parse(text)}
                                </H4>
                            );

                        case 'titleText':
                            return (
                                <Title
                                    key={text}
                                    {...elementProps}
                                >
                                    {parse(text)}
                                </Title>
                            );

                        case 'paragraph':
                        default:
                            return (
                                <P
                                    key={text}
                                    // @ts-ignore
                                    size="2xs"
                                    {...elementProps}
                                >
                                    {parseHtmlWithStyledLinks(text)}
                                </P>
                            );
                    }
                })}
        </div>
    ),
);

export default ContentText;
